import { DocumentMoveSectionKey } from "./documentMoveValidation";

const DOCUMENT_MOVE_QUERY_KEYS = [
  "proofOfIdsData",
  "ssnGetidentification",
  "income",
  "bankDoc",
  "otherIncomeDoc",
  "otherDoc",
  "otherDocData",
  "getIDValidationCheck",
  "getEmployerValidationCheck",
  "getBankValidationCheck",
  "getMetaData",
];

// Which react-query key prefixes feed each document section's UI (thumbnail
// lists, validation banners, metadata rules). Used to refetch only the
// sections a move/delete actually touched instead of every document query on
// the page — invalidateQueries resolves only after active queries refetch, so
// over-invalidating directly extends how long the move UI stays busy.
const SECTION_QUERY_KEYS: Record<DocumentMoveSectionKey, string[]> = {
  id: ["proofOfIdsData", "getIDValidationCheck", "getMetaData"],
  ssn: ["ssnGetidentification", "getMetaData"],
  paystub: ["income", "getEmployerValidationCheck", "getMetaData"],
  bank: ["bankDoc", "getBankValidationCheck", "getMetaData"],
  other_income: ["otherIncomeDoc", "getMetaData"],
  other: ["otherDoc", "otherDocData"],
};

// True when the query key mentions the given id anywhere — react-admin keys
// carry it in different places depending on the hook: useGetOne puts it in
// { id }, getMetaData in { meta: { trn_id } }, useGetList/<List> in
// { filter: { trn_id } }. Exact value comparison, not substring matching, so
// trn "123" never matches trn "1234".
const queryKeyReferencesId = (value: unknown, id: string): boolean => {
  if (value == null) return false;
  if (typeof value === "string" || typeof value === "number") {
    return String(value) === id;
  }
  if (Array.isArray(value)) {
    return value.some((entry) => queryKeyReferencesId(entry, id));
  }
  if (typeof value === "object") {
    return Object.values(value).some((entry) => queryKeyReferencesId(entry, id));
  }
  return false;
};

// Refetch only the queries behind the given sections, scoped to one
// applicant. ApplicantDetailEdit mounts a DocumentAnalysis instance per
// applicant tab simultaneously; a bare prefix invalidation would refetch
// every tab's copy of these queries.
export const invalidateDocumentQueriesForSections = async (
  queryClient: any,
  sections: Array<DocumentMoveSectionKey | "trash">,
  options?: { trnId?: string | number | null; extraQueryKeys?: string[] }
) => {
  const queryKeys = new Set<string>(options?.extraQueryKeys ?? []);
  sections.forEach((section) => {
    (SECTION_QUERY_KEYS[section as DocumentMoveSectionKey] ?? []).forEach(
      (queryKey) => queryKeys.add(queryKey)
    );
  });
  if (!queryKeys.size) return;
  const trnId = options?.trnId == null ? null : String(options.trnId);
  await queryClient.invalidateQueries({
    predicate: (query: any) =>
      queryKeys.has(query?.queryKey?.[0]) &&
      (trnId === null || queryKeyReferencesId(query?.queryKey, trnId)),
  });
};

// Full-width fallback for callers that don't know which section a document
// came from (the legacy MoveButton dialog). Everything that can know its
// sections should use invalidateDocumentQueriesForSections instead.
export const invalidateDocumentMoveQueries = async (queryClient: any) => {
  await Promise.all(
    DOCUMENT_MOVE_QUERY_KEYS.map((queryKey) =>
      queryClient.invalidateQueries([queryKey])
    )
  );
};
